import IMPairingCard from './IMPairingCard';
import IMNotificationSettings from './IMNotificationSettings';
import QQConfigForm from './QQConfigForm';
/**
 * IM Settings Component
 * Configuration UI for DingTalk, Feishu, WeCom, Telegram, Discord and NIM IM bots
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { XMarkIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { RootState } from '../../store';
import { imService } from '../../services/im';
import { setDingTalkConfig, setFeishuConfig, setTelegramConfig, setDiscordConfig, setWechatWorkConfig, setQQConfig } from '../../store/slices/imSlice';
import { i18nService, LanguageType } from '../../services/i18n';
import type { IMPlatform, IMConnectivityCheck, IMConnectivityTestResult, IMGatewayConfig } from '../../types/im';
import { getVisibleIMPlatforms } from '../../utils/regionFilter';

// Platform metadata
const platformMeta: Record<IMPlatform, { label: string; logo: string }> = {
  qq: { label: 'QQ', logo: 'qq.svg' },
  wechat: { label: '微信', logo: 'wechat.svg' },
  dingtalk: { label: '钉钉', logo: 'dingding.png' },
  feishu: { label: '飞书', logo: 'feishu.png' },
  telegram: { label: 'Telegram', logo: 'telegram.svg' },
  discord: { label: 'Discord', logo: 'discord.svg' },
  wechatwork: { label: '企业微信', logo: 'wecom.svg' },
};

const verdictColorClass: Record<IMConnectivityTestResult['verdict'], string> = {
  pass: 'bg-green-500/15 text-green-600 dark:text-green-400',
  warn: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300',
  fail: 'bg-red-500/15 text-red-600 dark:text-red-400',
};

const checkLevelColorClass: Record<IMConnectivityCheck['level'], string> = {
  pass: 'text-green-600 dark:text-green-400',
  info: 'text-sky-600 dark:text-sky-400',
  warn: 'text-yellow-700 dark:text-yellow-300',
  fail: 'text-red-600 dark:text-red-400',
};

const IMSettings: React.FC<{ instanceId?: string; platform?: IMPlatform | 'all' }> = ({ instanceId = 'default', platform: instancePlatform = 'all' }) => {
  const dispatch = useDispatch();
  const { config, status, isLoading, error } = useSelector((state: RootState) => state.im);
  const [activePlatform, setActivePlatform] = useState<IMPlatform>('dingtalk');
  const [testingPlatform, setTestingPlatform] = useState<IMPlatform | null>(null);
  const [connectivityResults, setConnectivityResults] = useState<Partial<Record<IMPlatform, IMConnectivityTestResult>>>({});
  const [connectivityModalPlatform, setConnectivityModalPlatform] = useState<IMPlatform | null>(null);
  const [language, setLanguage] = useState<LanguageType>(i18nService.getLanguage());

  // Subscribe to language changes
  useEffect(() => {
    const unsubscribe = i18nService.subscribe(() => {
      setLanguage(i18nService.getLanguage());
    });
    return unsubscribe;
  }, []);

  // Initialize IM service and subscribe status updates
  useEffect(() => {
    void imService.init(instanceId);
    return () => {
      imService.destroy();
    };
  }, [instanceId]);

  // Handle DingTalk config change
  const handleDingTalkChange = (field: 'clientId' | 'clientSecret', value: string) => {
    dispatch(setDingTalkConfig({ [field]: value }));
  };

  // Handle Feishu config change
  const handleFeishuChange = (field: 'appId' | 'appSecret', value: string) => {
    dispatch(setFeishuConfig({ [field]: value }));
  };

  // Handle Telegram config change
  const handleTelegramChange = (field: 'botToken', value: string) => {
    dispatch(setTelegramConfig({ [field]: value }));
  };

  // Handle Discord config change
  const handleDiscordChange = (field: 'botToken', value: string) => {
    dispatch(setDiscordConfig({ [field]: value }));
  };

  // Handle WeCom config change
  const handleWechatWorkChange = (field: 'corpId' | 'agentId' | 'corpSecret' | 'token' | 'encodingAESKey' | 'callbackPort', value: string | number) => {
    dispatch(setWechatWorkConfig({ [field]: value }));
  };

  // Save config on blur
  const handleSaveConfig = async () => {
    await imService.updateConfig(instancePlatform === 'all' ? config : { [instancePlatform]: config[instancePlatform], settings: config.settings });
  };

  const getCheckTitle = (code: IMConnectivityCheck['code']): string => {
    return i18nService.t(`imConnectivityCheckTitle_${code}`);
  };

  const getCheckSuggestion = (check: IMConnectivityCheck): string | undefined => {
    if (check.suggestion) {
      return check.suggestion;
    }
    if (check.code === 'gateway_running' && check.level === 'pass') {
      return undefined;
    }
    const suggestion = i18nService.t(`imConnectivityCheckSuggestion_${check.code}`);
    if (suggestion.startsWith('imConnectivityCheckSuggestion_')) {
      return undefined;
    }
    return suggestion;
  };

  const formatTestTime = (timestamp: number): string => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return String(timestamp);
    }
  };

  const runConnectivityTest = async (
    platform: IMPlatform,
    configOverride?: Partial<IMGatewayConfig>
  ) => {
    setTestingPlatform(platform);
    const result = await imService.testGateway(platform, configOverride);
    if (result) {
      setConnectivityResults((prev) => ({ ...prev, [platform]: result }));
    }
    setTestingPlatform(null);
  };

  // Toggle gateway on/off and persist enabled state
  const toggleGateway = async (platform: IMPlatform) => {
    const isEnabled = config[platform]?.enabled ?? false;
    const newEnabled = !isEnabled;

    // 保存成功后才启停；失败保持真实服务端状态，不伪造在线或离线。
    if (newEnabled) {
      if (!await imService.updateConfig({ [platform]: config[platform] })) return;
      await imService.startGateway(platform);
    } else {
      await imService.stopGateway(platform);
    }
  };

  const dingtalkConnected = status.dingtalk.connected;
  const feishuConnected = status.feishu.connected;
  const telegramConnected = status.telegram.connected;
  const discordConnected = status.discord.connected;
  const wechatworkConnected = status.wechatwork.connected;

  // Compute visible platforms based on language
  const platforms = useMemo<IMPlatform[]>(() => {
    return instancePlatform === 'all' ? getVisibleIMPlatforms(language) as IMPlatform[] : [instancePlatform];
  }, [language, instancePlatform]);

  // Ensure activePlatform is always in visible platforms when language changes
  useEffect(() => {
    if (platforms.length > 0 && !platforms.includes(activePlatform)) {
      // If current activePlatform is not visible, switch to first visible platform
      setActivePlatform(platforms[0]);
    }
  }, [platforms, activePlatform]);

  // Check if platform can be started
  const canStart = (platform: IMPlatform): boolean => {
    if (platform === 'wechat') return !!(config.wechat?.botToken && config.wechat?.accountId);
    if (platform === 'qq') return !!(config.qq?.appId && config.qq?.appSecret);
    if (platform === 'dingtalk') {
      return !!(config.dingtalk.clientId && config.dingtalk.clientSecret);
    }
    if (platform === 'telegram') {
      return !!config.telegram.botToken;
    }
    if (platform === 'discord') {
      return !!config.discord.botToken;
    }
    if (platform === 'wechatwork') {
      return !!(config.wechatwork.corpId && config.wechatwork.corpSecret && config.wechatwork.agentId);
    }
    return !!(config.feishu.appId && config.feishu.appSecret);
  };

  // Get platform enabled state (persisted toggle state)
  const isPlatformEnabled = (platform: IMPlatform): boolean => {
    return config[platform]?.enabled ?? false;
  };

  // Get platform connection status (runtime state)
  const getPlatformConnected = (platform: IMPlatform): boolean => {
    if (platform === 'dingtalk') return dingtalkConnected;
    if (platform === 'telegram') return telegramConnected;
    if (platform === 'discord') return discordConnected;
    if (platform === 'wechatwork') return wechatworkConnected;
    if (platform === 'wechat') return status.wechat?.connected ?? false;
    if (platform === 'qq') return status.qq?.connected ?? false;
    return feishuConnected;
  };

  // Get platform transient starting status
  const getPlatformStarting = (platform: IMPlatform): boolean => {
    if (platform === 'discord') return status.discord.starting;
    return false;
  };

  const handleConnectivityTest = async (platform: IMPlatform) => {
    setConnectivityModalPlatform(platform);
    await runConnectivityTest(platform, {
      [platform]: config[platform],
    } as Partial<IMGatewayConfig>);
  };

  // Handle platform toggle
  const handlePlatformToggle = (platform: IMPlatform) => {
    const isEnabled = isPlatformEnabled(platform);
    // Can toggle ON if credentials are present, can always toggle OFF
    const canToggle = isEnabled || canStart(platform);
    if (canToggle && !isLoading) {
      toggleGateway(platform);
    }
  };

  const renderConnectivityTestButton = (platform: IMPlatform) => (
    <button
      type="button"
      onClick={() => handleConnectivityTest(platform)}
      disabled={isLoading || testingPlatform === platform}
      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-xl border dark:border-claude-darkBorder border-claude-border dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-[0.98]"
    >
      <svg className="h-3.5 w-3.5 mr-1.5" viewBox="0 0 1024 1024" fill="currentColor"><path d="M574 665.4c-3.1-3.1-8.2-3.1-11.3 0L446.5 781.6c-53.8 53.8-144.6 59.5-204 0-59.5-59.5-53.8-150.2 0-204l116.2-116.2c3.1-3.1 3.1-8.2 0-11.3l-39.8-39.8c-3.1-3.1-8.2-3.1-11.3 0L191.4 526.5c-84.6 84.6-84.6 221.5 0 306s221.5 84.6 306 0l116.2-116.2c3.1-3.1 3.1-8.2 0-11.3L574 665.4zM832.6 191.4c-84.6-84.6-221.5-84.6-306 0L410.3 307.6c-3.1 3.1-3.1 8.2 0 11.3l39.7 39.7c3.1 3.1 8.2 3.1 11.3 0l116.2-116.2c53.8-53.8 144.6-59.5 204 0 59.5 59.5 53.8 150.2 0 204L665.3 562.6c-3.1 3.1-3.1 8.2 0 11.3l39.8 39.8c3.1 3.1 8.2 3.1 11.3 0l116.2-116.2c84.5-84.6 84.5-221.5 0-306.1z"/><path d="M610.1 372.3c-3.1-3.1-8.2-3.1-11.3 0L372.3 598.7c-3.1 3.1-3.1 8.2 0 11.3l39.6 39.6c3.1 3.1 8.2 3.1 11.3 0l226.4-226.4c3.1-3.1 3.1-8.2 0-11.3l-39.5-39.6z"/></svg>
      {testingPlatform === platform
        ? i18nService.t('imConnectivityTesting')
        : connectivityResults[platform]
          ? i18nService.t('imConnectivityRetest')
          : i18nService.t('imConnectivityTest')}
    </button>
  );

  useEffect(() => {
    if (!connectivityModalPlatform) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setConnectivityModalPlatform(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [connectivityModalPlatform]);

  return (
    <div className="flex h-full flex-col gap-4 sm:flex-row">
      {error && <div role="alert" className="rounded-lg border border-red-300 p-3 text-sm text-red-600">{error}</div>}
      {/* Platform List - Left Side */}
      <div className="flex max-w-full shrink-0 gap-2 overflow-x-auto sm:block sm:w-48 sm:border-r dark:border-claude-darkBorder border-claude-border pr-3 space-y-2 overflow-y-auto">
        {platforms.map((platform) => {
          const meta = platformMeta[platform];
          const isEnabled = isPlatformEnabled(platform);
          const isConnected = getPlatformConnected(platform) || getPlatformStarting(platform);
          return (
            <button type="button" aria-pressed={activePlatform === platform}
              key={platform}
              onClick={() => setActivePlatform(platform)}
              className={`flex w-full items-center p-2 rounded-xl cursor-pointer transition-colors ${
                activePlatform === platform
                  ? 'bg-claude-accent/10 dark:bg-claude-accent/20 border border-claude-accent/30'
                  : 'bg-claude-surfaceHover/80 dark:bg-claude-darkSurface/55 dark:bg-gradient-to-br dark:from-claude-darkSurface/70 dark:to-claude-darkSurfaceHover/70 hover:bg-claude-surface dark:hover:from-claude-darkSurface/80 dark:hover:to-claude-darkSurfaceHover/80 dark:border-claude-darkBorder/70 border-claude-border/80 border'
              }`}
            >
              <div className="flex flex-1 items-center">
                <div className="mr-2 flex h-7 w-7 items-center justify-center">
                  <img
                    src={meta.logo}
                    alt={meta.label}
                    className="w-6 h-6 object-contain"
                  />
                </div>
                <span className={`text-sm font-medium truncate ${
                  activePlatform === platform
                    ? 'text-claude-accent'
                    : 'dark:text-claude-darkText text-claude-text'
                }`}>
                  {i18nService.t(platform)}
                </span>
              </div>
              <div className="flex items-center ml-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    isEnabled && isConnected
                      ? 'bg-green-500'
                      : isEnabled
                        ? 'bg-yellow-500'
                        : 'bg-gray-400 dark:bg-gray-600'
                  }`}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Platform Settings - Right Side */}
      <div className="flex-1 min-w-0 space-y-4 overflow-y-auto">
        {/* Header with status */}
        <div className="flex items-center justify-between pb-3 border-b dark:border-claude-darkBorder/60 border-claude-border/60">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-white dark:bg-claude-darkBorder/30 p-1">
                <img
                  src={platformMeta[activePlatform].logo}
                  alt={platformMeta[activePlatform].label}
                  className="w-4 h-4 object-contain"
                />
              </div>
              <h3 className="text-sm font-medium dark:text-claude-darkText text-claude-text">
                {`${i18nService.t(activePlatform)}${i18nService.t('settings')}`}
              </h3>
            </div>
            <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              getPlatformConnected(activePlatform) || getPlatformStarting(activePlatform)
                ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                : 'bg-gray-500/15 text-gray-500 dark:text-gray-400'
            }`}>
              {getPlatformConnected(activePlatform)
                ? i18nService.t('connected')
                : getPlatformStarting(activePlatform)
                  ? (i18nService.t('starting') || '启动中')
                  : i18nService.t('disconnected')}
            </div>
            {['dingtalk', 'feishu', 'wechatwork'].includes(activePlatform) && (
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); window.electron.shell.openExternal('https://boclaw.ai/guide'); }}
                className="text-[11px] text-claude-accent hover:underline flex items-center gap-0.5"
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                {i18nService.t('imConfigGuide')}
              </a>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {isPlatformEnabled(activePlatform) ? i18nService.t('enabled') : i18nService.t('disabled')}
            </span>
            <button type="button" role="switch" aria-label={i18nService.t(activePlatform)} aria-checked={isPlatformEnabled(activePlatform)} disabled={isLoading || !(isPlatformEnabled(activePlatform) || canStart(activePlatform))}
              className={`w-7 h-4 rounded-full flex items-center transition-colors ${
                isPlatformEnabled(activePlatform)
                  ? (getPlatformConnected(activePlatform) || getPlatformStarting(activePlatform) ? 'bg-green-500' : 'bg-yellow-500')
                  : 'dark:bg-claude-darkBorder bg-claude-border'
              } ${!(isPlatformEnabled(activePlatform) || canStart(activePlatform)) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={!(isPlatformEnabled(activePlatform) || canStart(activePlatform)) ? i18nService.t('imToggleNeedTest') : undefined}
              onClick={() => handlePlatformToggle(activePlatform)}
            >
              <div
                className={`w-3 h-3 rounded-full bg-white shadow-md transform transition-transform ${
                  isPlatformEnabled(activePlatform) ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>

        {/* DingTalk Settings */}
        {activePlatform === 'dingtalk' && (
          <div className="space-y-3">
            {/* Client ID */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                Client ID (AppKey)
              </label>
              <input
                type="text"
                value={config.dingtalk.clientId}
                onChange={(e) => handleDingTalkChange('clientId', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="dingxxxxxx"
              />
            </div>

            {/* Client Secret */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                Client Secret (AppSecret)
              </label>
              <input
                type="password"
                aria-label="dingtalk clientSecret" autoComplete="off"
                value={config.dingtalk.clientSecret}
                onChange={(e) => handleDingTalkChange('clientSecret', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="••••••••••••"
              />
            </div>

            <div className="pt-1">
              {renderConnectivityTestButton('dingtalk')}
            </div>

            {/* Error display */}
            {status.dingtalk.lastError && (
              <div className="text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">
                {status.dingtalk.lastError}
              </div>
            )}
          </div>
        )}

        {/* Feishu Settings */}
        {activePlatform === 'feishu' && (
          <div className="space-y-3">
            {/* App ID */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                App ID
              </label>
              <input
                type="text"
                value={config.feishu.appId}
                onChange={(e) => handleFeishuChange('appId', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="cli_xxxxx"
              />
            </div>

            {/* App Secret */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                App Secret
              </label>
              <input
                type="password"
                aria-label="feishu appSecret" autoComplete="off"
                value={config.feishu.appSecret}
                onChange={(e) => handleFeishuChange('appSecret', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="••••••••••••"
              />
            </div>

            <div className="pt-1">
              {renderConnectivityTestButton('feishu')}
            </div>

            {/* Error display */}
            {status.feishu.error && (
              <div className="text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">
                {status.feishu.error}
              </div>
            )}
          </div>
        )}

        {/* Telegram Settings */}
        {activePlatform === 'telegram' && (
          <div className="space-y-3">
            {/* Bot Token */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                Bot Token
              </label>
              <input
                type="password"
                aria-label="telegram botToken" autoComplete="off"
                value={config.telegram.botToken}
                onChange={(e) => handleTelegramChange('botToken', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
              />
              <p className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
                {i18nService.t('telegramTokenHint')}
              </p>
            </div>

            <div className="pt-1">
              {renderConnectivityTestButton('telegram')}
            </div>

            {/* Bot username display */}
            {status.telegram.botUsername && (
              <div className="text-xs text-green-600 dark:text-green-400 bg-green-500/10 px-3 py-2 rounded-lg">
                Bot: @{status.telegram.botUsername}
              </div>
            )}

            {/* Error display */}
            {status.telegram.lastError && (
              <div className="text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">
                {status.telegram.lastError}
              </div>
            )}
          </div>
        )}

        {/* Discord Settings */}
        {activePlatform === 'discord' && (
          <div className="space-y-3">
            {/* Bot Token */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                Bot Token
              </label>
              <input
                type="password"
                aria-label="discord botToken" autoComplete="off"
                value={config.discord.botToken}
                onChange={(e) => handleDiscordChange('botToken', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="MTIzNDU2Nzg5MDEyMzQ1Njc4OQ..."
              />
              <p className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
                从 Discord Developer Portal 获取 Bot Token
              </p>
            </div>

            <div className="pt-1">
              {renderConnectivityTestButton('discord')}
            </div>

            {/* Bot username display */}
            {status.discord.botUsername && (
              <div className="text-xs text-green-600 dark:text-green-400 bg-green-500/10 px-3 py-2 rounded-lg">
                Bot: {status.discord.botUsername}
              </div>
            )}

            {/* Error display */}
            {status.discord.lastError && (
              <div className="text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">
                {status.discord.lastError}
              </div>
            )}
          </div>
        )}

        {/* WeCom Settings */}
        {activePlatform === 'wechatwork' && (
          <div className="space-y-3">
            {/* Corp ID */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                企业 ID (Corp ID)
              </label>
              <input
                type="text"
                value={config.wechatwork.corpId}
                onChange={(e) => handleWechatWorkChange('corpId', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="ww0000000000000000"
              />
            </div>

            {/* Agent ID */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                应用 ID (Agent ID)
              </label>
              <input
                type="text"
                value={config.wechatwork.agentId}
                onChange={(e) => handleWechatWorkChange('agentId', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="1000000"
              />
            </div>

            {/* Corp Secret */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                应用 Secret (Corp Secret)
              </label>
              <input
                type="password"
                aria-label="wechatwork corpSecret" autoComplete="off"
                value={config.wechatwork.corpSecret}
                onChange={(e) => handleWechatWorkChange('corpSecret', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="••••••••••••"
              />
            </div>

            {/* Token */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                Token（回调验证）
              </label>
              <input
                type="password"
                aria-label="wechatwork token" autoComplete="off"
                value={config.wechatwork.token}
                onChange={(e) => handleWechatWorkChange('token', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="••••••••••••"
              />
            </div>

            {/* EncodingAESKey */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                EncodingAESKey（消息加解密）
              </label>
              <input
                type="password"
                aria-label="wechatwork encodingAESKey" autoComplete="off"
                value={config.wechatwork.encodingAESKey}
                onChange={(e) => handleWechatWorkChange('encodingAESKey', e.target.value)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="••••••••••••"
              />
            </div>

            {/* Callback Port */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                本地回调端口
              </label>
              <input
                type="number"
                value={config.wechatwork.callbackPort}
                onChange={(e) => handleWechatWorkChange('callbackPort', parseInt(e.target.value, 10) || 3978)}
                onBlur={handleSaveConfig}
                className="block w-full rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:border-claude-darkBorder/60 border-claude-border/60 border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-2 text-sm transition-colors"
                placeholder="3978"
                min={1024}
                max={65535}
              />
              <p className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
                在企业微信后台将回调地址配置为：<code className="text-xs">http(s)://your-domain:port/wechatwork</code>
              </p>
            </div>

            {/* Callback URL display */}
            {status.wechatwork.callbackUrl && (
              <div className="text-xs text-sky-600 dark:text-sky-400 bg-sky-500/10 px-3 py-2 rounded-lg">
                本地监听: {status.wechatwork.callbackUrl}
              </div>
            )}

            <div className="pt-1">
              {renderConnectivityTestButton('wechatwork')}
            </div>

            {/* Error display */}
            {status.wechatwork.lastError && (
              <div className="text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">
                {status.wechatwork.lastError}
              </div>
            )}
          </div>
        )}

        {activePlatform === 'wechat' && <div className="space-y-3">
          <IMPairingCard key={instanceId} platform="wechat" accountId={config.wechat.accountId} enabled={config.wechat.enabled} instanceId={instanceId} disabled={isLoading || config.wechat.enabled} />
          {status.wechat?.lastError && <p role="alert" className="text-xs text-red-500">{status.wechat.lastError}</p>}
        </div>}

        {activePlatform === 'qq' && <div className="space-y-3">
          <QQConfigForm config={config.qq} instanceId={instanceId} pairingAvailable={instancePlatform !== 'all'} disabled={isLoading || config.qq.enabled}
            onChange={patch => dispatch(setQQConfig(patch))} onSave={handleSaveConfig} />
          {renderConnectivityTestButton('qq')}
          {status.qq?.lastError && <p role="alert" className="text-xs text-red-500">{status.qq.lastError}</p>}
        </div>}

        <IMNotificationSettings key={`${instanceId}:${activePlatform}`} instanceId={instanceId} platform={activePlatform} />

        {connectivityModalPlatform && (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={() => setConnectivityModalPlatform(null)}
          >
            <div
              className="w-full max-w-2xl dark:bg-claude-darkSurface bg-claude-surface rounded-2xl shadow-modal border dark:border-claude-darkBorder border-claude-border overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b dark:border-claude-darkBorder border-claude-border flex items-center justify-between">
                <div className="text-sm font-semibold dark:text-claude-darkText text-claude-text">
                  {`${i18nService.t(connectivityModalPlatform)} ${i18nService.t('imConnectivitySectionTitle')}`}
                </div>
                <button
                  type="button"
                  aria-label={i18nService.t('close')}
                  onClick={() => setConnectivityModalPlatform(null)}
                  className="p-1 rounded-md dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary text-claude-textSecondary"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="p-4 max-h-[65vh] overflow-y-auto">
                {testingPlatform === connectivityModalPlatform ? (
                  <div className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
                    {i18nService.t('imConnectivityTesting')}
                  </div>
                ) : connectivityResults[connectivityModalPlatform] ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${verdictColorClass[connectivityResults[connectivityModalPlatform]!.verdict]}`}>
                        {connectivityResults[connectivityModalPlatform]!.verdict === 'pass' ? (
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                        ) : connectivityResults[connectivityModalPlatform]!.verdict === 'warn' ? (
                          <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                        ) : (
                          <XCircleIcon className="h-3.5 w-3.5" />
                        )}
                        {i18nService.t(`imConnectivityVerdict_${connectivityResults[connectivityModalPlatform]!.verdict}`)}
                      </div>
                      <div className="text-[11px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                        {`${i18nService.t('imConnectivityLastChecked')}: ${formatTestTime(connectivityResults[connectivityModalPlatform]!.testedAt)}`}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {connectivityResults[connectivityModalPlatform]!.checks.map((check, index) => (
                        <div
                          key={`${check.code}-${index}`}
                          className="rounded-lg border dark:border-claude-darkBorder/60 border-claude-border/60 px-2.5 py-2 dark:bg-claude-darkSurface/25 bg-white/70"
                        >
                          <div className={`text-xs font-medium ${checkLevelColorClass[check.level]}`}>
                            {getCheckTitle(check.code)}
                          </div>
                          <div className="mt-1 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
                            {check.message}
                          </div>
                          {getCheckSuggestion(check) && (
                            <div className="mt-1 text-[11px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                              {`${i18nService.t('imConnectivitySuggestion')}: ${getCheckSuggestion(check)}`}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
                    {i18nService.t('imConnectivityNoResult')}
                  </div>
                )}
              </div>

              <div className="px-4 py-3 border-t dark:border-claude-darkBorder border-claude-border flex items-center justify-end">
                {renderConnectivityTestButton(connectivityModalPlatform)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IMSettings;
